import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, Mail, MapPin, Award, CheckCircle, Calendar, DollarSign, TrendingUp, ArrowRight, Building2, Globe, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Projects = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const majorProjects = [
    {
      client: 'Google Inc',
      project: 'Networking & Access Control',
      status: 'Completed',
      year: '2022',
      location: 'Kenya',
      category: 'Network Infrastructure'
    },
    {
      client: 'Woolpert Inc (USA)',
      project: 'Digitization of Ghana Mapping Services',
      status: 'Completed',
      year: '2020',
      location: 'Ghana',
      category: 'Software Development'
    },
    {
      client: 'Seventh Day Theocratic World',
      project: 'Cell Tower Installation',
      status: 'Completed',
      year: '2023',
      location: 'Ghana',
      category: 'Telecommunication'
    },
    {
      client: 'Ministry of Roads & Transport',
      project: 'VoIP Telephony Services',
      status: 'Completed',
      year: '2020',
      location: 'Ghana',
      category: 'VoIP Solutions'
    },
    {
      client: 'FOCOS Hospital',
      project: 'Network Infrastructure Development',
      status: 'Completed',
      year: '2011-2017',
      location: 'Ghana',
      category: 'Network Infrastructure'
    },
    {
      client: 'ECG Ghana',
      project: 'IT Consulting on Sub-stations',
      status: 'Completed',
      year: '2020',
      location: 'Ghana',
      category: 'IT Consulting'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                alt="HNS Logo"
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#18246F]">HNS</h1>
                <p className="text-xs text-gray-600">...technology with infinite solutions</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Home</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">About</Link>
              <Link to="/services" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Services</Link>
              <Link to="/projects" className="text-[#18246F] font-semibold">Projects</Link>
              <Link to="/team" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Team</Link>
              <Link to="/contact" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Contact</Link>
              <Button onClick={() => navigate('/contact')} className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white">Let's Connect</Button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Home</Link>
              <Link to="/about" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">About</Link>
              <Link to="/services" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Services</Link>
              <Link to="/projects" className="block px-4 py-2 text-[#18246F] bg-gray-100 rounded-lg font-semibold">Projects</Link>
              <Link to="/team" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Team</Link>
              <Link to="/contact" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Contact</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#18246F] via-[#293074] to-[#3d4a8f] text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">Our Projects</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Major Project Portfolio</h1>
          <p className="text-xl text-white/90 max-w-3xl">Delivering enterprise-scale projects across multiple sectors with proven track record of excellence</p>
        </div>
      </div>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-[#8B2635] to-[#6d1d29] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold mb-2">150+</p>
              <p className="text-white/90">Projects Completed</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">$2.5M+</p>
              <p className="text-white/90">Total Project Value</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">50+</p>
              <p className="text-white/90">Clients Served</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">100%</p>
              <p className="text-white/90">Success Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* HNS Projects 2024-2025 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#3C74FF]/10 text-[#3C74FF] border-[#3C74FF]/20">Recent Projects</Badge>
            <h2 className="text-4xl font-bold text-[#18246F] mb-6">HNS Projects Implemented in <span className="text-[#3C74FF]">2024-2025</span></h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-justify">
              Though incorporated in 2004, Hyperlink Network Systems is becoming one of the best in IT and general office equipment in the country. We have successfully executed projects in our service area for both private and public-sector firms, and some of these are presented below:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {/* Project 1: Bank of Ghana */}
            <Card className="border-2 border-gray-200 hover:border-[#3C74FF] hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <Badge className="bg-[#3C74FF] text-white">2024-2025</Badge>
                  <Building2 className="text-[#3C74FF]" size={24} />
                </div>
                <CardTitle className="text-xl text-[#18246F] mb-2">Bank of Ghana New Headquarters (Bank Square)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">CLIENT:</p>
                  <p className="text-gray-700">Ostec</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">SCOPE OF WORK:</p>
                  <p className="text-gray-700 text-sm">Installation of all data, voice, and IESS cables according to structured cabling and termination standards.</p>
                </div>
              </CardContent>
            </Card>

            {/* Project 2: Telecom Cell Site */}
            <Card className="border-2 border-gray-200 hover:border-[#3C74FF] hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <Badge className="bg-[#3C74FF] text-white">2024-2025</Badge>
                  <Globe className="text-[#3C74FF]" size={24} />
                </div>
                <CardTitle className="text-xl text-[#18246F] mb-2">Telecom Cell Site Civil & Electrical Works</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">CLIENT:</p>
                  <p className="text-gray-700">Afriwave Engineering Limited</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">SCOPE OF WORK:</p>
                  <p className="text-gray-700 text-sm">Decommissioning of existing cell site, palisade fence refurbishment works, and installation and configuration of solar structures.</p>
                </div>
              </CardContent>
            </Card>

            {/* Project 3: Consultancy & Survey */}
            <Card className="border-2 border-gray-200 hover:border-[#3C74FF] hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <Badge className="bg-[#3C74FF] text-white">2025</Badge>
                  <TrendingUp className="text-[#3C74FF]" size={24} />
                </div>
                <CardTitle className="text-xl text-[#18246F] mb-2">Consultancy & Survey</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">CLIENT:</p>
                  <p className="text-gray-700">Bennin Army Forces</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">SCOPE OF WORK:</p>
                  <p className="text-gray-700 text-sm">Conducting technical site survey for the construction of a proposed cell site.</p>
                </div>
              </CardContent>
            </Card>

            {/* Project 4: Cell Site Build */}
            <Card className="border-2 border-gray-200 hover:border-[#3C74FF] hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <Badge className="bg-[#3C74FF] text-white">2025</Badge>
                  <Building2 className="text-[#3C74FF]" size={24} />
                </div>
                <CardTitle className="text-xl text-[#18246F] mb-2">Cell Site Build</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">CLIENT:</p>
                  <p className="text-gray-700">Seventh Day Theoretical Congregation Moment @ Otiakrom</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-semibold mb-1">SCOPE OF WORK:</p>
                  <p className="text-gray-700 text-sm">Construction of a 50m cell site (Civil works, tower erection & electrical installation).</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Some of Our <span className="text-[#3C74FF]">Past Projects</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Our portfolio showcases successful implementations across various industries</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {majorProjects.map((project, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#3C74FF] hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge className="bg-[#3C74FF]/10 text-[#3C74FF] border-[#3C74FF]/20">{project.category}</Badge>
                    <Badge className="bg-green-100 text-green-700 border-green-200 flex items-center gap-1">
                      <CheckCircle size={14} />
                      {project.status}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl mb-2">{project.client}</CardTitle>
                  <p className="text-sm text-gray-600">{project.project}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <Calendar size={16} className="mr-1" /> Year:
                    </span>
                    <span className="font-semibold">{project.year}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <MapPin size={16} className="mr-1" /> Location:
                    </span>
                    <span className="font-semibold">{project.location}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center">
                      <CheckCircle size={16} className="mr-1 text-green-600" /> Status:
                    </span>
                    <span className="font-semibold text-green-600">{project.status}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery - Auto-scrolling Carousel */}
      <section className="py-20 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Project <span className="text-[#3C74FF]">Gallery</span></h2>
            <p className="text-gray-600">Visual showcase of our completed projects</p>
          </div>
          
          {/* Carousel Container */}
          <div className="relative">
            <div className="carousel-wrapper">
              <div className="carousel-track">
                {/* First set of images */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/tzdctizn_WhatsApp%20Image%202025-11-08%20at%2002.29.31_bb5ce4a9.jpg"
                  alt="Project 1"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/g0p6ryxn_WhatsApp%20Image%202025-11-08%20at%2002.29.31_ed34a519.jpg"
                  alt="Project 2"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/a80nsmtn_WhatsApp%20Image%202025-11-08%20at%2002.34.08_14f8a0dc.jpg"
                  alt="Project 3"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/an9i87s2_WhatsApp%20Image%202025-11-08%20at%2002.29.27_5cbb2483.jpg"
                  alt="Project 4"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/tj5mqgyl_WhatsApp%20Image%202025-11-08%20at%2002.29.29_6be46678.jpg"
                  alt="Project 5"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/6ha1ov5e_WhatsApp%20Image%202025-11-08%20at%2002.29.30_4510be1b.jpg"
                  alt="Project 6"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/echw9o3u_WhatsApp%20Image%202025-11-08%20at%2002.29.31_4b96db2b.jpg"
                  alt="Project 7"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/8jryt0bi_WhatsApp%20Image%202025-11-08%20at%2002.34.06_f94db00b.jpg"
                  alt="Project 8"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                
                {/* Duplicate set for seamless loop */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/tzdctizn_WhatsApp%20Image%202025-11-08%20at%2002.29.31_bb5ce4a9.jpg"
                  alt="Project 1"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/g0p6ryxn_WhatsApp%20Image%202025-11-08%20at%2002.29.31_ed34a519.jpg"
                  alt="Project 2"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/a80nsmtn_WhatsApp%20Image%202025-11-08%20at%2002.34.08_14f8a0dc.jpg"
                  alt="Project 3"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/an9i87s2_WhatsApp%20Image%202025-11-08%20at%2002.29.27_5cbb2483.jpg"
                  alt="Project 4"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/tj5mqgyl_WhatsApp%20Image%202025-11-08%20at%2002.29.29_6be46678.jpg"
                  alt="Project 5"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/6ha1ov5e_WhatsApp%20Image%202025-11-08%20at%2002.29.30_4510be1b.jpg"
                  alt="Project 6"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/echw9o3u_WhatsApp%20Image%202025-11-08%20at%2002.29.31_4b96db2b.jpg"
                  alt="Project 7"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-frontend/artifacts/8jryt0bi_WhatsApp%20Image%202025-11-08%20at%2002.34.06_f94db00b.jpg"
                  alt="Project 8"
                  className="carousel-image rounded-xl shadow-lg object-cover"
                />
              </div>
            </div>
          </div>
        </div>
        
        <style jsx>{`
          .carousel-wrapper {
            overflow: hidden;
            position: relative;
          }
          
          .carousel-track {
            display: flex;
            gap: 1.5rem;
            animation: scroll 40s linear infinite;
            width: max-content;
          }
          
          .carousel-track:hover {
            animation-play-state: paused;
          }
          
          .carousel-image {
            width: 400px;
            height: 280px;
            flex-shrink: 0;
          }
          
          @keyframes scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#18246F] mb-4">Start Your Next Project</h2>
          <p className="text-gray-600 mb-8">Let's discuss how we can bring your vision to life</p>
          <Button 
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white group"
          >
            Contact Us
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#18246F] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                  alt="HNS Logo"
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">HNS</h3>
                  <p className="text-xs text-gray-300">...technology with infinite solutions</p>
                </div>
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Leading the ICT industry with reliable information systems solutions. ISO 9001:2015 certified with over 20 years of excellence in technology delivery.
              </p>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Award size={16} />
                <span>Trusted by 50+ Organizations</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/projects" className="hover:text-white transition-colors">Projects</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Network Security</li>
                <li>Cloud Management</li>
                <li>Software Development</li>
                <li>VoIP Solutions</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <Mail size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>info@hnsgh.com</span>
                </li>
                <li className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>Accra, Ghana | Manassas, VA USA</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Hyperlink Network Systems Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a 
        href="tel:+15714064203"
        className="fixed bottom-8 left-8 bg-[#3C74FF] hover:bg-[#2d5fd9] text-white rounded-full p-4 shadow-2xl z-50 flex items-center gap-3 group transition-all duration-300 hover:scale-110"
      >
        <Phone size={24} className="animate-pulse" />
        <span className="hidden group-hover:inline-block text-sm font-semibold whitespace-nowrap">
          +1 (571) 406-4203
        </span>
      </a>
    </div>
  );
};

export default Projects;
